// pages/Admin/UsersAdminPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Stack, Typography, TextField, IconButton, Tooltip, Chip,
  Divider, Button, FormControl, InputLabel, Select, MenuItem, Checkbox,
  FormControlLabel, useMediaQuery, Avatar, Badge, InputAdornment, Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Link as LinkIcon, RotateCw as RefreshIcon } from 'lucide-react';
import { People, CheckCircle, Block, Search, Edit as EditIcon } from '@mui/icons-material';

import MainText from '../../components/MainText';
import CleanningButton from '../../components/CleanningButton';
import DatePick from '../../components/DatePick';
import CustomSwitch from '../../components/CustomSwitch';

import { addTab } from '../../tabs/tabsSlice';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../auth/AuthProvider';
import CustomModal from '../../components/CustomModal';
import CustomSnackBar from '../../components/CustomSnackBar';
import API, { RBAC, Accounts } from '../../services/api';

const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function formatAbsolute(dtStr) {
  if (!dtStr) return '—';
  const d = new Date(dtStr);
  if (Number.isNaN(d.getTime())) return '—';

  const dd = String(d.getDate()).padStart(2, '0');
  const mm = MONTHS_SHORT[d.getMonth()];
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');

  return `${dd} ${mm} ${yyyy}, ${hh}:${mi}`;
}

function formatRelative(dtStr) {
  if (!dtStr) return '—';
  const d = new Date(dtStr);
  if (Number.isNaN(d.getTime())) return '—';

  const diff = Date.now() - d.getTime();

  if (diff < 0) return formatAbsolute(dtStr);

  const diffMinutes = Math.floor(diff / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'Hace segundos';
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
  if (diffHours < 24) return `Hace ${diffHours} h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;

  const dd = String(d.getDate()).padStart(2, '0');
  const mm = MONTHS_SHORT[d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd} ${mm} ${yyyy}`;
}
const formatYMDHMS = (v) => {
  if (v === null || v === undefined) return "Sin fecha";
  const s = String(v).trim();
  if (!s) return "Sin fecha";

  // 1) Coincidencia exacta YYYY-MM-DD HH:mm:ss (o similar)
  // Esta regex es más flexible para incluir T, Z o espacios
  const fullMatch = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(Z)?)?/
  );

  if (fullMatch) {
    // Usaremos la Date nativa para manejar la conversión de zona horaria (como 'Z' o ISO)
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");

      return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
    }
  }

  // 2) Último intento: Date nativa para cualquier otro formato válido
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");

    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  // 3) No se pudo formatear
  return "Sin fecha";
};
function AssignCompanyRoleForm({
  setOpen,
  targetUser,
  companies = [],
  roles = [],
  assign,
  setAssign,
  onSubmit,
}) {
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackMsg, setSnackMsg] = React.useState("");
  const [snackSev, setSnackSev] = React.useState("info");
  const [loading, setLoading] = React.useState(false);

  const handleSave = async () => {
    if (!assign?.company_id) {
      setSnackMsg("Selecciona una empresa.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }
    if (!assign?.role) {
      setSnackMsg("Selecciona un rol.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }

    try {
      setLoading(true);
      await Promise.resolve(onSubmit?.(assign));

      setSnackMsg("Asignación guardada correctamente.");
      setSnackSev("success");
      setSnackOpen(true);

      setTimeout(() => setOpen(false), 350);
    } catch (e) {
      setSnackMsg("No se pudo guardar la asignación. Intenta de nuevo.");
      setSnackSev("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = React.useMemo(() => {
    const currentInCompany = (targetUser?.user_companies || [])
      .find(uc => (uc.company?.id || uc.company_id) === assign.company_id);

    return roles.filter(r => {
      if (!currentInCompany) return true;
      if (String(assign.role || "") === String(currentInCompany?.role?.id || "")) return true;
      return String(r.id) !== String(currentInCompany?.role?.id || "");
    });
  }, [roles, targetUser, assign.company_id, assign.role]);

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {targetUser ? `${(targetUser.first_name || '').trim()} ${(targetUser.last_name || '').trim()} (${targetUser.email || targetUser.username})` : ''}
        </Typography>

        <Divider />

        <FormControl fullWidth size="small">
          <InputLabel>Empresa</InputLabel>
          <Select
            label="Empresa"
            value={assign.company_id ?? ""}
            onChange={(e) => setAssign(s => ({ ...s, company_id: e.target.value, role: "" }))}
          >
            {companies.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={!assign.company_id}>
          <InputLabel>Rol</InputLabel>
          <Select
            label="Rol"
            value={assign.role ?? ""}
            onChange={(e) => setAssign(s => ({ ...s, role: e.target.value }))}
          >
            {filteredRoles.map(r => (
              <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={!!assign.makeCurrent}
              onChange={(e) => setAssign(s => ({ ...s, makeCurrent: e.target.checked }))}
            />
          }
          label="Marcar como activa (current)"
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={() => setOpen(false)} disabled={loading}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !(assign.company_id && assign.role)}
          >
            {loading ? "Guardando…" : "Guardar"}
          </Button>
        </Stack>
      </Stack>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </>
  );
}


export default function UsersAdminPage() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const dispatch = useDispatch();
  const { isSuperuser, permissions, user: authUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);

  const [assignOpen, setAssignOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [assign, setAssign] = useState({ company_id: '', role: '', makeCurrent: false });
  const [initialAssign, setInitialAssign] = useState({ company_id: '', role: '' });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState('info');

  const [filters, setFilters] = useState({
    search: '',
    startDate: null,
    endDate: null,
    status: 'all',
    active: true,
    company_id: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Extra permissions panel state
  const canAssignExtras = isSuperuser || (Array.isArray(permissions) && permissions.includes('asign_extra_permissions'));
  const [extraPanelOpen, setExtraPanelOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availablePerms, setAvailablePerms] = useState([]);
  const [extraAssignments, setExtraAssignments] = useState([]);
  const [extraCompanyId, setExtraCompanyId] = useState('');
  const [extraSelectedIds, setExtraSelectedIds] = useState([]);
  const [extraLoading, setExtraLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);

  useEffect(() => {
    dispatch(addTab({ label: 'Usuarios', path: '/admin/users' }));
  }, [dispatch]);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(filters.search), 500);
    return () => clearTimeout(h);
  }, [filters.search]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  const clearAll = () => {
    setFilters({
      search: '',
      startDate: null,
      endDate: null,
      status: 'all',
      active: true,
      company_id: '',
    });
  };

  useEffect(() => {
    (async () => {
      try {
        if (isSuperuser) {
          const { data } = await RBAC.companies.list();
          const list = Array.isArray(data) ? data : (data?.results || []);
          setCompanies(list || []);
        } else {
          const { data } = await RBAC.companiesMine();
          const list = Array.isArray(data) ? data : (data?.results || []);
          const norm = (list || []).map(x => (x && x.company ? x.company : x)).filter(Boolean);
          setCompanies(norm);
        }
      } catch {
        setCompanies([]);
        setSnackMsg('No se pudieron cargar las empresas.');
        setSnackSev('error');
        setSnackOpen(true);
      }
    })();
  }, [isSuperuser]);

  // Cargar catálogo de permisos cuando se abre el panel de extras
  useEffect(() => {
    if (!canAssignExtras || !extraPanelOpen) return;
    (async () => {
      try {
        const { data } = await RBAC.permissions();
        setAvailablePerms(Array.isArray(data) ? data : []);
      } catch {
        setAvailablePerms([]);
      }
    })();
  }, [canAssignExtras, extraPanelOpen]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        company_id: filters.company_id || undefined,
        start_date: filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : undefined,
        end_date: filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : undefined,
      };

      if (filters.status !== 'all') {
        params.active = filters.status === 'active' ? 'true' : 'false';
      } else {
        params.active = filters.active ? undefined : 'false';
      }

      const { data } = await Accounts.users(params);
      const rows = Array.isArray(data) ? data : [];
      setUsers(rows);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters]);

  useEffect(() => { loadUsers(); }, [loadUsers]);


  const handleRowClick = useCallback((params) => {
    const id = params.id;
    setSelectionModel([id]);
    const u = users.find(x => String(x.id) === String(id));
    setSelectedUser(u || null);
  }, [users]);

  const openAssign = (u) => {
    setTargetUser(u);
    const list = Array.isArray(u?.user_companies) ? u.user_companies : [];
    const current = list.find(uc => uc.current) || list[0];
    const pre = current ? { company_id: current.company?.id || '', role: current.role?.id || '', makeCurrent: !!current.current } : { company_id: '', role: '', makeCurrent: false };
    setAssign({ company_id: pre.company_id, role: pre.role, makeCurrent: pre.makeCurrent });
    setInitialAssign({ company_id: pre.company_id, role: pre.role });
    setAssignOpen(true);
  };

  const openAssignNew = (u) => {
    setTargetUser(u);
    setAssign({ company_id: '', role: '', makeCurrent: false });
    setInitialAssign({ company_id: '', role: '' });
    setAssignOpen(true);
  };

  const openAssignForCompany = (u, companyId) => {
    setTargetUser(u);
    const list = Array.isArray(u?.user_companies) ? u.user_companies : [];
    const found = list.find(uc => (uc.company?.id || uc.company_id) === companyId);
    const pre = found ? { company_id: companyId, role: found.role?.id || '', makeCurrent: !!found.current } : { company_id: companyId, role: '', makeCurrent: false };
    setAssign({ company_id: pre.company_id, role: pre.role, makeCurrent: pre.makeCurrent });
    setInitialAssign({ company_id: pre.company_id, role: pre.role });
    setAssignOpen(true);
  };
  const closeAssign = () => setAssignOpen(false);
  const loadRoles = async (cid) => {
    if (!cid) { setRoles([]); return; }
    try {
      const { data } = await RBAC.rolesByCompany(cid);
      setRoles(data || []);
    } catch { setRoles([]); }
  };
  useEffect(() => { if (assign.company_id) loadRoles(assign.company_id); }, [assign.company_id]);

  const handleAssign = async () => {
    if (!(targetUser?.id && assign.company_id && assign.role)) return;
    try {
      await RBAC.assignUserCompany({
        user_id: targetUser.id,
        company_id: assign.company_id,
        role: assign.role,
        is_active: true,
        extra_permission_ids: [],
        current: assign.makeCurrent,
      });
      closeAssign();
      loadUsers();
    } catch { }
  };

  // Cuando hay usuario seleccionado y panel abierto, cargar sus asignaciones con permisos extra
  useEffect(() => {
    if (!canAssignExtras || !extraPanelOpen || !selectedUser?.id) return;
    (async () => {
      setExtraLoading(true);
      try {
        const { data } = await RBAC.userCompanies(selectedUser.id);
        const list = Array.isArray(data) ? data : [];
        setExtraAssignments(list);
        const first = list[0];
        const cid = first?.company?.id || '';
        setExtraCompanyId(cid);
        if (cid && Array.isArray(first?.extra_permissions)) {
          setExtraSelectedIds(first.extra_permissions.map(p => p.id));
        } else {
          setExtraSelectedIds([]);
        }
      } catch {
        setExtraAssignments([]);
        setExtraSelectedIds([]);
      } finally {
        setExtraLoading(false);
      }
    })();
  }, [canAssignExtras, extraPanelOpen, selectedUser]);

  // Sincronizar selección de permisos al cambiar de empresa en el panel
  useEffect(() => {
    if (!extraCompanyId) return;
    const uc = extraAssignments.find(x => (x.company?.id || x.company_id) === extraCompanyId);
    if (uc && Array.isArray(uc.extra_permissions)) {
      setExtraSelectedIds(uc.extra_permissions.map(p => p.id));
    } else {
      setExtraSelectedIds([]);
    }
  }, [extraCompanyId, extraAssignments]);

  const handleToggleExtraPerm = (permId) => {
    setExtraSelectedIds(prev =>
      prev.includes(permId) ? prev.filter(id => id !== permId) : [...prev, permId]
    );
  };


  const handleSaveExtras = async () => {
    if (!canAssignExtras || !selectedUser?.id || !extraCompanyId) return;
    const uc = extraAssignments.find(x => (x.company?.id || x.company_id) === extraCompanyId);
    if (!uc || !(uc.role && uc.role.id)) return;
    try {
      setExtraLoading(true);
      await RBAC.assignUserCompany({
        user_id: selectedUser.id,
        company_id: extraCompanyId,
        role: uc.role.id,
        is_active: uc.is_active ?? true,
        extra_permission_ids: extraSelectedIds,
        current: uc.current ?? false,
      });
      setSnackMsg('Permisos extra actualizados.');
      setSnackSev('success');
      setSnackOpen(true);
      // recargar asignaciones para reflejar cambios
      const { data } = await RBAC.userCompanies(selectedUser.id);
      setExtraAssignments(Array.isArray(data) ? data : []);
    } catch {
      setSnackMsg('No se pudieron actualizar los permisos extra.');
      setSnackSev('error');
      setSnackOpen(true);
    } finally {
      setExtraLoading(false);
    }
  };

  /* ===== Celdas UI ===== */
  const avatarForRow = (id) => {
    if (!id) return null;
    try {
      return localStorage.getItem(`user_avatar_${id}`);
    } catch {
      return null;
    }
  };

  const NameCell = ({ row }) => {
    const initials =
      `${row.first_name?.[0] ?? ''}${row.last_name?.[0] ?? ''}`.trim().toUpperCase()
      || row.username?.slice(0, 2).toUpperCase()
      || 'US';
    const avatarUrl = avatarForRow(row.id);
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 220, height: '100%' }}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: (row?.user_companies || []).some(uc => uc.current) ? 'success.main' : 'grey.400',
              border: '1.5px solid white'
            }} />
          }
        >
          <Avatar src={avatarUrl || undefined} sx={{ bgcolor: 'primary.main', color: 'white', width: 36, height: 36, fontSize: '0.9rem', fontWeight: 600 }}>
            {!avatarUrl && initials}
          </Avatar>
        </Badge>
        <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 700, lineHeight: 1.15 }}>
            {`${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || row.username}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', lineHeight: 1.15 }}>
            @{row.username}
          </Typography>
        </Box>
      </Stack>
    );
  };

  const EmailCell = ({ value }) => (
    <Chip label={value || '—'} size="small" variant="outlined" sx={{ borderRadius: 2, fontWeight: 600, maxWidth: 240 }} />
  );

  const CompaniesCell = ({ row }) => {
    const list = row.user_companies || [];
    if (!list.length) return <Chip size="small" label="Sin asignaciones" variant="outlined" />;
    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
        {list.map((uc, idx) => (
          <Chip
            key={`${uc.id}-${idx}`}
            size="small"
            label={`${uc.company?.name || 'Empresa'} — ${uc.role?.name || 'Sin rol'}${uc.current ? ' • Activa' : ''}`}
            color={uc.current ? 'primary' : 'default'}
            variant={uc.current ? 'filled' : 'outlined'}
            sx={{ borderRadius: 1.5, cursor: 'pointer' }}
            onClick={() => openAssignForCompany(row, uc.company?.id)}
          />
        ))}
      </Stack>
    );
  };

  const StateChip = ({ active }) => (
    <Chip size="small" label={active ? 'Activo' : 'Inactivo'} color={active ? 'success' : 'default'} variant={active ? 'filled' : 'outlined'} />
  );

  const DateCell = ({ value, emptyHint = '—' }) => {
    const isValidDate = value && !Number.isNaN(new Date(value).getTime());

    const CenteredWrapper = ({ children }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        {children}
      </Box>
    );

    if (!value || !isValidDate) {
      return (
        <CenteredWrapper>
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            {emptyHint}
          </Typography>
        </CenteredWrapper>
      );
    }

    const rel = formatRelative(value);
    const abs = formatAbsolute(value);

    return (
      <CenteredWrapper>
        <Tooltip title={abs} arrow>
          <Chip
            size="small"
            label={rel}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          />
        </Tooltip>
      </CenteredWrapper>
    );
  };


  const ActionsCell = ({ row }) => {
    const canToggle = isSuperuser || (Array.isArray(permissions) && permissions.includes('manage_users'));
    const refresh = () => loadUsers();
    return (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Editar empresa / rol">
          <IconButton size="small" onClick={() => openAssign(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Asignar a empresa / rol">
          <IconButton size="small" onClick={() => openAssignNew(row)}>
            <LinkIcon size={18} />
          </IconButton>
        </Tooltip>
        {canToggle && (
          row.is_active ? (
            <Tooltip title="Desactivar usuario">
              <IconButton size="small" color="error" onClick={async () => { try { await Accounts.setUserActive(row.id, false); } catch { } refresh(); }}>
                <Block fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Activar usuario">
              <IconButton size="small" color="success" onClick={async () => { try { await Accounts.setUserActive(row.id, true); } catch { } refresh(); }}>
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        )}
      </Stack>
    );
  };

  const columns = useMemo(() => ([
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Nombre', flex: 1, minWidth: 220, sortable: false, renderCell: (p) => <NameCell row={p.row} /> },
    { field: 'email', headerName: 'Email', width: 240, renderCell: (p) => <EmailCell value={p.value} /> },
    { field: 'is_active', headerName: 'Estado', width: 120, sortable: false, renderCell: (p) => <StateChip active={!!p.row?.is_active} /> },
    {
      field: 'last_login',
      headerName: 'Último acceso',
      width: 170,
      renderCell: (p) => <DateCell value={p.row?.last_login || null} emptyHint="Nunca" />
    },
    {
      field: 'date_joined',
      headerName: 'Creado',
      width: 160,
      // renderCell: (p) => <DateCell value={p.row?.date_joined || null} />
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', width: '100%' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
            {formatYMDHMS(row?.date_joined || null)}
          </Typography>
        </Box>
      )

    },
    { field: 'companies', headerName: 'Empresas / Rol', flex: 1, minWidth: 260, sortable: false, renderCell: (p) => <CompaniesCell row={p.row} /> },
    { field: 'actions', headerName: 'Acciones', width: 110, sortable: false, renderCell: (p) => <ActionsCell row={p.row} /> }
  ]), [isMobile, isSuperuser, permissions]);

  const gridHeight = isMobile ? 'calc(100vh - 230px)' : 'calc(100vh - 260px)';

  const companyName = filters.company_id
    ? companies.find(x => String(x.id) === String(filters.company_id))?.name || `ID: ${filters.company_id}`
    : '';

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
        alignItems: 'center', gap: 2, width: '100%', position: 'relative'
      }}>
        <Box sx={{ flexGrow: 1 }}>
          <MainText title="Usuarios" subtitle="Listado y asignaciones" icon={People} width="auto" />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {filters.status !== 'all' && (
            <Chip
              label={`Estado: ${filters.status === 'active' ? 'Activos' : 'Inactivos'}`}
              onDelete={() => handleFilterChange('status', 'all')}
            />
          )}
          {filters.search && (
            <Chip label={`Búsqueda: "${filters.search}"`} onDelete={() => handleFilterChange('search', '')} />
          )}
          {filters.startDate && (
            <Chip
              label={`Desde: ${new Date(filters.startDate).toLocaleDateString()}`}
              onDelete={() => handleFilterChange('startDate', null)}
            />
          )}
          {filters.endDate && (
            <Chip
              label={`Hasta: ${new Date(filters.endDate).toLocaleDateString()}`}
              onDelete={() => handleFilterChange('endDate', null)}
            />
          )}
          {filters.company_id && (
            <Chip
              label={`Empresa: ${companyName}`}
              onDelete={() => handleFilterChange('company_id', '')}
            />
          )}
          {!filters.active && filters.status === 'all' && (
            <Chip
              label="Inactivos"
              color="error"
              onDelete={() => handleFilterChange('active', true)}
            />
          )}
          {filters.status === 'all' && !filters.search && !filters.startDate && !filters.endDate && !filters.company_id && filters.active && (
            <Chip label="Mostrando todos los usuarios" color="info" />
          )}
        </Box>

        <CleanningButton onClick={clearAll} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', gap: 1,
          alignItems: 'center', justifyContent: 'flex-start', marginRight: '30px'
        }}>
          <DatePick
            label="Fecha inicio"
            size="small"
            value={filters.startDate}
            onChange={(date) => handleFilterChange('startDate', date)}
            allowHistoricalDates={true}
            allowFutureDates={false}
            maxWidth={200}
          />
          <DatePick
            label="Fecha fin"
            size="small"
            value={filters.endDate}
            onChange={(date) => handleFilterChange('endDate', date)}
            allowHistoricalDates={true}
            allowFutureDates={false}
            maxWidth={200}
          />

          <TextField
            variant="outlined"
            size="small"
            sx={{ padding: '5px', width: 'auto' }}
            placeholder="Buscar usuario..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <CustomSwitch
            name="active"
            label={filters.active ? 'Activo' : 'Inactivo'}
            onChange={(name, value) => handleFilterChange(name, value)}
            initialValue={filters.active}
          />

          <FormControl sx={{ minWidth: 220, padding: '5px' }}>
            <InputLabel>Empresa</InputLabel>
            <Select
              size="small"
              label="Empresa"
              value={filters.company_id}
              onChange={(e) => handleFilterChange('company_id', e.target.value)}
            >
              <MenuItem value="">(Todas)</MenuItem>
              {companies.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {canAssignExtras && (
            <Tooltip title="Asignar permisos extra">
              <Button
                onClick={() => setExtraPanelOpen(prev => !prev)}
                startIcon={<EditIcon fontSize="small" />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  px: 1.8,
                  py: 0.6,
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: extraPanelOpen ? 'transparent' : 'divider',
                  bgcolor: extraPanelOpen ? 'rgba(16,23,60,0.95)' : 'background.paper',
                  color: extraPanelOpen ? '#fff' : 'text.primary',
                  boxShadow: extraPanelOpen ? '0 4px 12px rgba(0,0,0,0.18)' : 'none',
                  '&:hover': {
                    bgcolor: extraPanelOpen ? 'rgba(16,23,60,1)' : 'action.hover',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: 0.6,
                  },
                }}
                variant="text"
              >
                Permisos extra
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Refrescar listado">
            <IconButton onClick={loadUsers}><RefreshIcon size={18} /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 1 }}>
      <Box sx={{ height: gridHeight, width: extraPanelOpen ? '65%' : '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
            getRowId={(r) => r.id}
            rowSelectionModel={selectionModel}
            onRowClick={handleRowClick}
            disableRowSelectionOnClick={false}
            hideFooterSelectedRowCount
            loading={loading}
            disableColumnFilter
            disableColumnMenu
            pageSizeOptions={[10, 20, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            density={isMobile ? 'compact' : 'standard'}
            getRowHeight={() => (isMobile ? 56 : 64)}
            columnHeaderHeight={50}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#10173c',
                color: '#ffffff',
              },
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover': {
                backgroundColor: '#10173c !important',
              },
              '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator': {
                color: '#ffffff !important',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
                padding: isMobile ? '0 6px' : '0 10px',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'rgba(16, 23, 60, 0.08)',
                  cursor: 'pointer',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(16, 23, 60, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 23, 60, 0.15)',
                  },
                },
              },
              '& .MuiDataGrid-virtualScroller': {
                overscrollBehavior: 'contain',
              },
            }}
          />


        </Box>
        {extraPanelOpen && canAssignExtras && (
          <Paper
            elevation={2}
            sx={{
              flex: 1,
              p: 2,
              minWidth: 280,
              maxWidth: 380,
              height: gridHeight,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Permisos extra por usuario
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ajusta solo los permisos adicionales al rol.
                </Typography>
              </Box>
              <Chip
                size="small"
                icon={<People fontSize="small" />}
                label={selectedUser ? 'Usuario seleccionado' : 'Sin selección'}
                sx={{ borderRadius: 2, fontSize: '0.7rem' }}
                variant={selectedUser ? 'outlined' : 'filled'}
                color={selectedUser ? 'primary' : 'default'}
              />
            </Stack>

            {/* Usuario */}
            <Box
              sx={{
                mt: 1,
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'grey.50',
                border: '1px dashed',
                borderColor: 'divider',
                minHeight: 48,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: selectedUser ? 'text.primary' : 'text.disabled' }}
              >
                {selectedUser
                  ? `${(selectedUser.first_name || '').trim()} ${(selectedUser.last_name || '').trim()} · ${(selectedUser.email || selectedUser.username)}`
                  : 'Selecciona un usuario en la tabla para editar sus permisos extra.'}
              </Typography>
            </Box>

            {/* Empresa */}
            <FormControl
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              disabled={!selectedUser}
            >
              <InputLabel>Empresa</InputLabel>
              <Select
                label="Empresa"
                value={extraCompanyId || ''}
                onChange={(e) => setExtraCompanyId(e.target.value)}
              >
                {(extraAssignments || []).map(uc => (
                  <MenuItem key={uc.id} value={uc.company?.id || uc.company_id}>
                    {uc.company?.name || 'Empresa'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Lista de permisos */}
            <Box
              sx={{
                flex: 1,
                mt: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                overflowY: 'auto',
                bgcolor: 'background.default',
              }}
            >
              {extraLoading && (
                <Typography variant="body2" color="text.secondary">
                  Cargando permisos...
                </Typography>
              )}

              {!extraLoading && (!selectedUser || !extraCompanyId) && (
                <Typography variant="body2" color="text.secondary">
                  Selecciona una empresa para ver y editar los permisos extra.
                </Typography>
              )}

              {!extraLoading && selectedUser && extraCompanyId && (
                <Stack spacing={0.5}>
                  {(availablePerms || []).map(p => {
                    const uc = extraAssignments.find(
                      x => (x.company?.id || x.company_id) === extraCompanyId
                    );
                    const baseIds = new Set((uc?.role?.permissions || []).map(pp => pp.id));
                    const isBase = baseIds.has(p.id);
                    const checked = isBase || extraSelectedIds.includes(p.id);

                    return (
                      <Box
                        key={p.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1.5,
                          bgcolor: isBase
                            ? 'rgba(16,23,60,0.04)'
                            : extraSelectedIds.includes(p.id)
                              ? 'rgba(176, 145, 72, 0.06)' // dorado MUY suave
                              : 'transparent',
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={isBase}
                              onChange={() => !isBase && handleToggleExtraPerm(p.id)}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.8rem',
                                color: isBase
                                  ? 'text.secondary'
                                  : extraSelectedIds.includes(p.id)
                                    ? 'text.primary'
                                    : 'text.secondary',
                              }}
                            >
                              {p.description || p.code}
                            </Typography>
                          }
                        />
                        {isBase && (
                          <Chip
                            label="Del rol"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 999,
                              fontSize: '0.65rem',
                              ml: 1,
                            }}
                          />
                        )}
                        {!isBase && extraSelectedIds.includes(p.id) && (
                          <Chip
                            label="Extra"
                            size="small"
                            sx={{
                              borderRadius: 999,
                              fontSize: '0.65rem',
                              ml: 1,
                              bgcolor: 'rgba(176,145,72,0.12)', // doradito elegante
                              color: '#6b562b',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}

                  {Array.isArray(availablePerms) && availablePerms.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No hay permisos configurados en el catálogo.
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>

            {/* Footer actions */}
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setExtraPanelOpen(false);
                  setSelectedUser(null);
                  setSelectionModel([]);
                }}
              >
                Cerrar
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveExtras}
                disabled={extraLoading || !selectedUser || !extraCompanyId}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontSize: '0.78rem',
                  px: 2,
                }}
              >
                Guardar cambios
              </Button>
            </Stack>
          </Paper>
        )}

      </Box>


      <CustomModal
        open={assignOpen}
        setOpen={setAssignOpen}
        title="Asignar/Editar empresa y rol"
        component={AssignCompanyRoleForm}
        componentProps={{
          targetUser,
          companies,
          roles,
          assign,
          setAssign,
          onSubmit: handleAssign,
        }}
        width={isMobile ? '92%' : '520px'}
      />

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />

    </Box>
  );
}
